import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import BulkTableModal from './BulkTableModal';

const DropArea = styled.div<{ $active: boolean }>`
  padding: 28px 16px;
  margin-bottom: 16px;
  border: 2px dashed ${({ $active }) => ($active ? '#00703c' : '#505a5f')};
  background: ${({ $active }) => ($active ? '#e6f0eb' : '#f3f2f1')};
  text-align: center;
`;

const FileList = styled.ol`
  max-height: 200px;
  overflow: auto;
  padding: 0;
  list-style: none;
  li {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #b1b4b6;
  }
  span { overflow-wrap: anywhere; min-width: 0; }
  button { flex-shrink: 0; }
`;

type Props = {
  initialFiles?: File[];
  onClose: () => void;
  onFiles: (files: File[]) => void | Promise<void>;
};

export default function ExcelUploadDialog({ initialFiles = [], onClose, onFiles }: Props) {
  const [files, setFiles] = useState(initialFiles);
  const [active, setActive] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const invalidFiles = files.filter((file) => !file.name.toLowerCase().endsWith('.xlsx'));

  return createPortal(
    <BulkTableModal title="Upload Excel files" onClose={onClose} zIndex={1200}>
      <DropArea
        $active={active}
        onDragOver={(event) => {
          event.preventDefault();
          setActive(true);
        }}
        onDragLeave={() => setActive(false)}
        onDrop={(event) => {
          event.preventDefault();
          setActive(false);
          const added = Array.from(event.dataTransfer.files);
          setFiles((current) => [...current, ...added]);
        }}
      >
        <p className="govuk-body govuk-!-margin-bottom-0">Drag and drop Excel files here</p>
      </DropArea>
      <input
        ref={input}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        multiple
        hidden
        aria-label="Select Excel files"
        onChange={(event) => {
          const added = Array.from(event.target.files || []);
          setFiles((current) => [...current, ...added]);
          event.target.value = '';
        }}
      />
      <button
        type="button"
        className="govuk-button govuk-button--secondary"
        onClick={() => input.current?.click()}
      >
        Select files
      </button>
      <p className="govuk-body-s govuk-!-margin-top-4" role="status">
        {files.length} {files.length === 1 ? 'file' : 'files'} selected
      </p>
      {files.length > 0 && (
        <FileList className="govuk-body-s" aria-label="Selected files">
          {files.map((file, index) => (
            <li key={index}>
              <span>{file.name}</span>
              <button
                type="button"
                className="govuk-link govuk-link--no-visited-state"
                style={{ border: 0, background: 'none', cursor: 'pointer', font: 'inherit' }}
                aria-label={`Remove ${file.name}`}
                onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}
              >
                Remove
              </button>
            </li>
          ))}
        </FileList>
      )}
      <p className="govuk-hint">XLSX files only. Rows will be combined in the order shown.</p>
      {invalidFiles.length > 0 && (
        <p className="govuk-error-message" role="alert">
          Select an XLSX file. Remove unsupported files to continue:{' '}
          {invalidFiles.map((file) => file.name).join(', ')}.
        </p>
      )}
      <div className="govuk-button-group govuk-!-margin-top-4">
        <button
          type="button"
          className="govuk-button"
          disabled={files.length === 0 || invalidFiles.length > 0}
          onClick={() => {
            onClose();
            void onFiles(files);
          }}
        >
          Continue
        </button>
        <button type="button" className="govuk-button govuk-button--secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </BulkTableModal>,
    document.body,
  );
}
